import { useCallback, useMemo } from 'react';
import { useProfessionalCategories, getCategoryByIdFromData, NormalizedCategory } from './useProfessionalCategories';

/**
 * Centralized hook for category lookups from DB
 * Replaces static getCategoriesByIds from data/categories.ts
 */
export function useCategoryHelpers() {
  const { data: categories, isLoading, error } = useProfessionalCategories(true);

  const getCategoryById = useCallback((id: string): NormalizedCategory | undefined => {
    if (!categories || categories.length === 0) return undefined;
    return getCategoryByIdFromData(categories, id);
  }, [categories]);

  const getCategoriesByIds = useCallback((ids: string[]): NormalizedCategory[] => {
    if (!categories || categories.length === 0 || !ids || ids.length === 0) return [];
    return ids
      .map(id => getCategoryByIdFromData(categories, id))
      .filter((cat): cat is NormalizedCategory => cat !== undefined);
  }, [categories]);

  const getCategoryName = useCallback((id: string, lang: 'it' | 'en' | 'es' = 'it'): string => {
    if (!categories || categories.length === 0) return id;
    const cat = getCategoryById(id);
    if (!cat) return id;
    return cat[lang]?.name || cat.it?.name || id;
  }, [categories, getCategoryById]);

  const getCategoryNames = useCallback((ids: string[], lang: 'it' | 'en' | 'es' = 'it'): string[] => {
    if (!ids || ids.length === 0) return [];
    return ids.map(id => getCategoryName(id, lang));
  }, [getCategoryName]);

  // Check if categories are ready to use
  const isReady = !isLoading && categories && categories.length > 0;

  return {
    categories,
    isLoading,
    isReady,
    error,
    getCategoryById,
    getCategoriesByIds,
    getCategoryName,
    getCategoryNames,
  };
}

/**
 * Type for category with language-specific name for display
 */
export interface DisplayCategory {
  id: string;
  icon: string;
  name: string;
}

/**
 * Get display-ready categories for a specific language
 */
export function useDisplayCategories(categoryIds: string[], lang: 'it' | 'en' | 'es' = 'it') {
  const { getCategoriesByIds, isLoading, isReady } = useCategoryHelpers();
  
  const displayCategories = useMemo((): DisplayCategory[] => {
    if (!isReady || !categoryIds || categoryIds.length === 0) return [];
    const cats = getCategoriesByIds(categoryIds);
    return cats.map(cat => ({
      id: cat.id,
      icon: cat.icon || '',
      name: cat[lang]?.name || cat.it?.name || cat.id,
    }));
  }, [categoryIds, lang, getCategoriesByIds, isReady]);

  return { displayCategories, isLoading, isReady };
}
